import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import SearchableSiteSelect from "./SearchableSiteSelect";
import "./horizonTable.css";
import "./AssetManagementPage.css";

function AssetManagementPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [assets, setAssets] = useState([]);
  const [labels, setLabels] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [statusFilter, setStatusFilter] = useState("all");
  const [siteFilter, setSiteFilter] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [page, setPage] = useState(1);
  const [showMenu, setShowMenu] = useState(false);
  const [googleMapsReady, setGoogleMapsReady] = useState(false);
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const pageSize = 25;
  const menuRef = useRef(null);

  useEffect(() => {
    let alive = true;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const apiUrl = process.env.REACT_APP_API_URL || "";
        const baseUrl = apiUrl.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl;
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };
        const [assetsRes, labelsRes, ticketsRes, sitesRes] = await Promise.all([
          fetch(`${baseUrl}/api/asset`, { headers }),
          fetch(`${baseUrl}/api/asset-management/labels`, { headers }),
          fetch(`${baseUrl}/api/asset/tickets`, { headers }),
          fetch(`${baseUrl}/api/sites`, { headers }),
        ]);
        const [assetsData, labelsData, ticketsData, sitesData] = await Promise.all([
          assetsRes.json().catch(() => ({})),
          labelsRes.json().catch(() => ({})),
          ticketsRes.json().catch(() => ({})),
          sitesRes.json().catch(() => ({})),
        ]);
        if (!alive) return;
        if (!assetsRes.ok || !assetsData?.success) {
          setError(assetsData?.message || "Failed to load assets");
          setAssets([]);
        } else {
          setAssets(Array.isArray(assetsData.data) ? assetsData.data : []);
        }
        if (labelsRes.ok && labelsData?.success) setLabels(Array.isArray(labelsData.data) ? labelsData.data : []);
        if (ticketsRes.ok && ticketsData?.success) setTickets(Array.isArray(ticketsData.data) ? ticketsData.data : []);
        if (sitesRes.ok && sitesData?.success) setSites(Array.isArray(sitesData.data) ? sitesData.data : []);
      } catch {
        if (alive) {
          setError("Failed to load asset dashboard");
          setAssets([]);
          setLabels([]);
          setTickets([]);
          setSites([]);
        }
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    function handleOutsideClick(event) {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    }
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    if (!googleMapsApiKey) return;
    if (window.google?.maps) {
      setGoogleMapsReady(true);
      return;
    }
    const scriptId = "google-maps-site-overview-script";
    const existing = document.getElementById(scriptId);
    if (existing) {
      existing.addEventListener("load", () => setGoogleMapsReady(true));
      return;
    }
    const script = document.createElement("script");
    script.id = scriptId;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => setGoogleMapsReady(true);
    document.body.appendChild(script);
  }, [googleMapsApiKey]);

  const normalizedAssets = useMemo(() => {
    return assets.map((a) => {
      const assetStatus = String(a.assetStatus || "").toLowerCase();
      const status =
        assetStatus === "retired"
          ? "retired"
          : assetStatus === "active"
            ? "active"
            : a.deviceCondition === "Fault"
              ? "fault"
              : a.deviceCondition === "Maintenance required"
                ? "maintenance"
                : "active";
      const type = String(a.manufacturer || a.modelNumber || a.deviceName || "General");
      const site = String(a.siteName || "Unknown");
      const client = String(a.clientName || "Unknown");
      return {
        ...a,
        normalizedStatus: status,
        normalizedType: type,
        normalizedSite: site,
        normalizedClient: client,
        lastScanAt: a.lastScannedAt || a.updatedAt || a.createdAt || null,
      };
    });
  }, [assets]);

  const faultsBySite = useMemo(() => {
    const openFaultsByLabel = {};
    tickets.forEach((t) => {
      const key = String(t.labelCode || "");
      openFaultsByLabel[key] = (openFaultsByLabel[key] || 0) + (String(t.status || "").toLowerCase() === "closed" ? 0 : 1);
    });

    const allSiteRows = Array.isArray(sites)
      ? sites.map((s) => ({
          siteId: String(s?._id || ""),
          site: String(s?.siteName || "Unknown"),
          assets: 0,
          faults: 0,
          maintained: 0,
        }))
      : [];

    const siteIndexById = new Map(allSiteRows.map((s, idx) => [String(s.siteId), idx]));
    const siteIndexByName = new Map(allSiteRows.map((s, idx) => [String(s.site || "").trim().toLowerCase(), idx]));

    normalizedAssets.forEach((a) => {
      const assetSiteId = String(a.siteId || "");
      const assetSiteName = String(a.normalizedSite || "").trim().toLowerCase();
      const idx = siteIndexById.has(assetSiteId)
        ? siteIndexById.get(assetSiteId)
        : siteIndexByName.has(assetSiteName)
          ? siteIndexByName.get(assetSiteName)
          : -1;
      if (idx >= 0) {
        allSiteRows[idx].assets += 1;
        allSiteRows[idx].faults += openFaultsByLabel[a.labelCode] || 0;
        if (Boolean(a.repairMaintenanceRequired)) {
        } else {
          allSiteRows[idx].maintained += 1;
        }
      }
    });

    return allSiteRows.sort((x, y) => y.assets - x.assets);
  }, [normalizedAssets, tickets, sites]);

  const faultTypeBreakdown = useMemo(() => {
    const map = {};
    tickets.forEach((t) => {
      if (String(t.status || "").toLowerCase() === "closed") return;
      const rawType = String(t.issueType || "").toLowerCase();
      const key =
        rawType === "fault"
          ? "fault_reported"
          : rawType === "maintenance"
            ? "maintenance_required"
            : "other_issue";
      if (!map[key]) {
        map[key] = { name: key, count: 0, highCritical: 0 };
      }
      map[key].count += 1;
      const sev = String(t.severity || "").toLowerCase();
      if (sev === "high" || sev === "critical") {
        map[key].highCritical += 1;
      }
    });
    return Object.values(map)
      .sort((a, b) => b.count - a.count);
  }, [tickets]);

  const stats = useMemo(() => {
    const total = normalizedAssets.length;
    const active = normalizedAssets.filter((a) => a.normalizedStatus === "active").length;
    const openFaults = tickets.filter((t) => String(t.status || "").toLowerCase() !== "closed").length;
    const highCritical = tickets.filter((t) => {
      const s = String(t.status || "").toLowerCase();
      const sev = String(t.severity || "").toLowerCase();
      return s !== "closed" && (sev === "high" || sev === "critical");
    }).length;
    const unassigned = labels.filter((x) => x.status === "unassigned").length;
    const addedThisMonth = normalizedAssets.filter((a) => {
      if (!a.createdAt) return false;
      const d = new Date(a.createdAt);
      const now = new Date();
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length;
    return { total, active, openFaults, highCritical, unassigned, addedThisMonth };
  }, [normalizedAssets, tickets, labels]);

  const statusSplit = useMemo(() => {
    const active = normalizedAssets.filter((a) => String(a.assetStatus || "").toLowerCase() === "active").length;
    const retired = normalizedAssets.filter((a) => String(a.assetStatus || "").toLowerCase() === "retired").length;
    const fault = normalizedAssets.filter((a) => {
      if (String(a.assetStatus || "").toLowerCase() === "retired") return false;
      const condition = String(a.deviceCondition || "").toLowerCase();
      return condition === "fault" || condition === "maintenance required";
    }).length;
    return { active, fault, retired };
  }, [normalizedAssets]);

  const filteredTableRows = useMemo(() => {
    let rows = [...normalizedAssets];
    if (statusFilter !== "all") rows = rows.filter((r) => r.normalizedStatus === statusFilter);
    if (siteFilter) rows = rows.filter((r) => r.normalizedSite === siteFilter);
    if (clientFilter) rows = rows.filter((r) => r.normalizedClient === clientFilter);
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) =>
        [r.deviceName, r.normalizedType, r.normalizedSite, r.normalizedClient, r.location, r.serialNumber, r.labelCode]
          .map((x) => String(x || "").toLowerCase())
          .join(" ")
          .includes(q)
      );
    }
    rows.sort((a, b) => {
      if (sortBy === "site") return a.normalizedSite.localeCompare(b.normalizedSite);
      if (sortBy === "lastScan") return new Date(b.lastScanAt || 0) - new Date(a.lastScanAt || 0);
      if (sortBy === "status") return a.normalizedStatus.localeCompare(b.normalizedStatus);
      return String(a.deviceName || "").localeCompare(String(b.deviceName || ""));
    });
    return rows;
  }, [normalizedAssets, statusFilter, siteFilter, clientFilter, search, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filteredTableRows.length / pageSize));
  const pagedRows = filteredTableRows.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, siteFilter, clientFilter, sortBy]);

  const handleResetTableFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setSortBy("name");
    setSiteFilter("");
    setClientFilter("");
    setPage(1);
  };

  const sitePoints = useMemo(() => buildSitePoints(faultsBySite, sites, normalizedAssets), [faultsBySite, sites, normalizedAssets]);
  const topSites = useMemo(() => faultsBySite.slice(0, 8), [faultsBySite]);

  const handlePrintLabels = () => {
    navigate("/admin/asset-management/qr-labels");
  };

  return (
    <div className="asset-dash-root">
      <div className="asset-dash-header">
        <h2>Assets</h2>
        <div className="asset-dash-actions">
          <button type="button" onClick={handlePrintLabels}>+ Print labels</button>
          <div className="asset-menu-wrap" ref={menuRef}>
            <button type="button" className="asset-menu-btn" onClick={() => setShowMenu((v) => !v)}>
              <i className="fa-solid fa-ellipsis" />
            </button>
            {showMenu ? (
              <div className="asset-menu-dropdown">
                <button type="button" onClick={() => { navigate("/admin/asset-management/qr-labels"); setShowMenu(false); }}>QR labels page</button>
                <button type="button" onClick={() => { navigate("/admin/asset-management/assets"); setShowMenu(false); }}>Assets list page</button>
                <button type="button" onClick={() => { navigate("/admin/asset-management/tickets"); setShowMenu(false); }}>Tickets page</button>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="asset-tabs">
        <button type="button" className={tab === "overview" ? "active" : ""} onClick={() => setTab("overview")}>Overview</button>
        <button type="button" className={tab === "map" ? "active" : ""} onClick={() => setTab("map")}>Map view</button>
        <button type="button" className={tab === "table" ? "active" : ""} onClick={() => setTab("table")}>Table view</button>
      </div>

      {loading ? <div className="asset-loading">Loading asset dashboard...</div> : null}
      {!loading && error ? <div className="asset-error">{error}</div> : null}

      {!loading && !error ? (
        <>
          {(tab === "overview" || tab === "map" || tab === "table") ? (
            <div className="asset-stats-grid">
              <StatCard title="TOTAL ASSETS" value={stats.total} note={`+${stats.addedThisMonth} this month`} noteColor="#8b1634" onClick={() => navigate("/admin/asset-management/assets")} />
              <StatCard title="ACTIVE" value={stats.active} note={`${toPercent(stats.active, stats.total)} of total`} noteColor="#8b1634" onClick={() => navigate("/admin/asset-management/assets")} />
              <StatCard title="OPEN FAULTS" value={stats.openFaults} note={`${stats.highCritical} high / critical`} noteColor="#b42352" onClick={() => navigate("/admin/asset-management/tickets")} />
              <StatCard title="UNASSIGNED QRS" value={stats.unassigned} note="labels in the field" noteColor="#a34763" onClick={() => navigate("/admin/asset-management/qr-labels")} />
            </div>
          ) : null}

          {tab === "overview" ? (
            <>
              <div className="asset-overview-grid">
                <div className="asset-panel">
                  <h3>Sites overview</h3>
                  <SiteMap
                    points={sitePoints}
                    onSiteClick={setSiteFilter}
                    onAssetClick={(labelCode) => navigate(`/admin/asset-management/assets?edit=${encodeURIComponent(labelCode)}`)}
                    onViewAllAssets={() => navigate("/admin/asset-management/assets")}
                    googleMapsReady={googleMapsReady}
                    hasApiKey={Boolean(googleMapsApiKey)}
                  />
                  <div className="asset-map-legend">
                    <span><b className="dot normal" /> Site — normal</span>
                    <span><b className="dot red" /> Site — high faults</span>
                  </div>
                  <div className="asset-map-sub">Pin size reflects asset count</div>
                </div>
                <div className="asset-right-stack">
                  <div className="asset-panel asset-clickable-panel" onClick={() => navigate("/admin/asset-management/assets")} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && navigate("/admin/asset-management/assets")}>
                    <h3>Asset status</h3>
                    <Donut total={stats.total} split={statusSplit} />
                  </div>
                  <div className="asset-panel asset-clickable-panel" onClick={() => navigate("/admin/asset-management/tickets")} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && navigate("/admin/asset-management/tickets")}>
                    <h3>Faults by type</h3>
                    <FaultTypeList data={faultTypeBreakdown} onItemClick={() => navigate("/admin/asset-management/tickets")} />
                  </div>
                </div>
              </div>
              <AssetTableSection
                rows={pagedRows}
                totalRows={filteredTableRows.length}
                page={page}
                totalPages={totalPages}
                onPrev={() => setPage((p) => Math.max(1, p - 1))}
                onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
                search={search}
                onSearch={setSearch}
                statusFilter={statusFilter}
                onStatusFilter={setStatusFilter}
                sortBy={sortBy}
                onSort={setSortBy}
                siteFilter={siteFilter}
                onSiteFilter={setSiteFilter}
                siteOptions={topSites.map((x) => x.site)}
                clientFilter={clientFilter}
                onClientFilter={setClientFilter}
                clientOptions={[...new Set(normalizedAssets.map((x) => x.normalizedClient).filter(Boolean))].sort()}
                onResetFilters={handleResetTableFilters}
              />
            </>
          ) : null}

          {tab === "map" ? (
            <div className="asset-panel">
              <h3>Map view</h3>
              <SiteMap
                points={sitePoints}
                onSiteClick={setSiteFilter}
                onAssetClick={(labelCode) => navigate(`/admin/asset-management/assets?edit=${encodeURIComponent(labelCode)}`)}
                onViewAllAssets={() => navigate("/admin/asset-management/assets")}
                large
                googleMapsReady={googleMapsReady}
                hasApiKey={Boolean(googleMapsApiKey)}
              />
            </div>
          ) : null}

          {tab === "table" ? (
            <AssetTableSection
              rows={pagedRows}
              totalRows={filteredTableRows.length}
              page={page}
              totalPages={totalPages}
              onPrev={() => setPage((p) => Math.max(1, p - 1))}
              onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
              search={search}
              onSearch={setSearch}
              statusFilter={statusFilter}
              onStatusFilter={setStatusFilter}
              sortBy={sortBy}
              onSort={setSortBy}
              siteFilter={siteFilter}
              onSiteFilter={setSiteFilter}
              siteOptions={topSites.map((x) => x.site)}
              clientFilter={clientFilter}
              onClientFilter={setClientFilter}
              clientOptions={[...new Set(normalizedAssets.map((x) => x.normalizedClient).filter(Boolean))].sort()}
              onResetFilters={handleResetTableFilters}
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function StatCard({ title, value, note, noteColor, onClick }) {
  return (
    <div
      className={`asset-stat-card ${onClick ? "clickable" : ""}`}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => e.key === "Enter" && onClick() : undefined}
    >
      <div className="asset-stat-title">{title}</div>
      <div className="asset-stat-value">{value}</div>
      <div className="asset-stat-note" style={{ color: noteColor }}>{note}</div>
    </div>
  );
}

function SiteMap({ points, onSiteClick, onAssetClick, onViewAllAssets, large, googleMapsReady, hasApiKey }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const ukCenter = useMemo(() => ({ lat: 54.5, lng: -3 }), []);
  const ukZoom = 5.8;
  const ukBounds = useMemo(
    () => ({
      north: 60.9,
      south: 49.8,
      west: -8.7,
      east: 2.2,
    }),
    []
  );

  useEffect(() => {
    if (!googleMapsReady || !mapRef.current || !window.google?.maps) return;
    if (!mapInstanceRef.current) {
      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
        center: large ? { lat: 20, lng: 0 } : ukCenter,
        zoom: large ? 2 : ukZoom,
        minZoom: large ? 2 : 5,
        maxZoom: 16,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        restriction: large
          ? undefined
          : {
              latLngBounds: ukBounds,
              strictBounds: true,
            },
      });
    }
  }, [googleMapsReady, ukBounds, ukCenter, ukZoom]);

  useEffect(() => {
    if (!mapInstanceRef.current || !window.google?.maps) return;
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];
    if (!points.length) return;

    const infoWindow = new window.google.maps.InfoWindow();
    const bounds = new window.google.maps.LatLngBounds();

    points.forEach((p) => {
      const marker = new window.google.maps.Marker({
        map: mapInstanceRef.current,
        position: { lat: p.lat, lng: p.lng },
        title: p.site,
        label: {
          text: String(p.assets),
          color: "#ffffff",
          fontWeight: "700",
          fontSize: "12px",
        },
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: Math.max(11, Math.min(18, p.r + 1)),
          fillColor: p.high ? "#b42352" : "#8b1634",
          fillOpacity: 0.95,
          strokeColor: "#ffffff",
          strokeWeight: 2,
        },
      });
      marker.addListener("click", () => {
        onSiteClick(p.site);
        setSelectedSite(p);
        infoWindow.close();
      });
      markersRef.current.push(marker);
    });
    points.forEach((p) => {
      bounds.extend({ lat: p.lat, lng: p.lng });
    });
    if (!large) {
      bounds.extend({ lat: ukBounds.south, lng: ukBounds.west });
      bounds.extend({ lat: ukBounds.north, lng: ukBounds.east });
    }
    mapInstanceRef.current.fitBounds(bounds, 20);
    if (!large) {
      mapInstanceRef.current.setCenter(ukCenter);
      mapInstanceRef.current.setZoom(ukZoom);
    }
    mapInstanceRef.current.setOptions({
      restriction: large
        ? undefined
        : {
            latLngBounds: ukBounds,
            strictBounds: true,
          },
    });
  }, [points, onSiteClick, ukBounds, ukCenter, ukZoom, large]);

  return (
    <div className={`asset-map-box ${large ? "large" : ""}`}>
      {!hasApiKey ? (
        <div className="asset-map-message">Google Maps key missing. Add `REACT_APP_GOOGLE_MAPS_API_KEY` in frontend `.env`.</div>
      ) : !googleMapsReady ? (
        <div className="asset-map-message">Loading Google Maps...</div>
      ) : !points.length ? (
        <div className="asset-map-message">No site coordinates available yet.</div>
      ) : (
        <div ref={mapRef} className="asset-google-map" />
      )}
      {selectedSite ? (
        <div className="asset-map-site-card">
          <div className="asset-map-site-card-head">
            <h4>{selectedSite.site}</h4>
            <button type="button" onClick={() => setSelectedSite(null)} aria-label="Close site details">×</button>
          </div>
          <div className="asset-map-site-card-grid">
            <div>
              <span>Assets</span>
              <strong>{selectedSite.assets}</strong>
            </div>
            <div>
              <span>Open faults</span>
              <strong>{selectedSite.faults}</strong>
            </div>
            <div>
              <span>Maintained</span>
              <strong>{selectedSite.maintained || 0}</strong>
            </div>
          </div>
          {Array.isArray(selectedSite.assetsPreview) && selectedSite.assetsPreview.length ? (
            <div className="asset-map-site-card-assets">
              <div className="asset-map-site-card-assets-title">Assets detail</div>
              <div className="asset-map-site-card-assets-list">
                {selectedSite.assetsPreview.map((a) => (
                  <div
                    key={a.labelCode || a.deviceName}
                    className={`asset-map-site-card-asset-row ${a.labelCode && onAssetClick ? "clickable" : ""}`}
                    onClick={() => a.labelCode && onAssetClick && onAssetClick(a.labelCode)}
                    role={a.labelCode && onAssetClick ? "button" : undefined}
                    tabIndex={a.labelCode && onAssetClick ? 0 : undefined}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && a.labelCode && onAssetClick) onAssetClick(a.labelCode);
                    }}
                  >
                    <div className="asset-map-site-card-asset-name">{a.deviceName}</div>
                    <div className="asset-map-site-card-asset-meta">{a.siteName}</div>
                  </div>
                ))}
                {selectedSite.assetsMoreCount ? (
                  <button
                    type="button"
                    className="asset-map-site-card-assets-more"
                    onClick={onViewAllAssets}
                  >
                    +{selectedSite.assetsMoreCount} more
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
      <div className="asset-map-overlay-text">Click marker to view site details · Red = high fault rate</div>
    </div>
  );
}

function Donut({ total, split }) {
  const activeAngle = total ? (split.active / total) * 360 : 0;
  const faultAngle = total ? (split.fault / total) * 360 : 0;
  const retiredAngle = Math.max(0, 360 - activeAngle - faultAngle);
  const bg = `conic-gradient(#8b1634 0deg ${activeAngle}deg, #b42352 ${activeAngle}deg ${activeAngle + faultAngle}deg, #d7a9b5 ${activeAngle + faultAngle}deg ${activeAngle + faultAngle + retiredAngle}deg)`;
  return (
    <div className="asset-donut-wrap">
      <div className="asset-donut" style={{ background: bg }}>
        <div className="asset-donut-inner">
          <strong>{total}</strong>
          <span>total</span>
        </div>
      </div>
      <div className="asset-donut-legend">
        <p><i style={{ background: "#8b1634" }} /> Active {split.active}</p>
        <p><i style={{ background: "#b42352" }} /> Fault {split.fault}</p>
        <p><i style={{ background: "#d7a9b5" }} /> Retired {split.retired}</p>
      </div>
    </div>
  );
}

function FaultTypeList({ data, onItemClick }) {
  if (!data.length) return <div className="asset-empty">No open faults</div>;
  return (
    <div className="asset-fault-list">
      {data.slice(0, 6).map((item) => (
        <div
          key={item.name}
          className={onItemClick ? "asset-fault-row-clickable" : ""}
          onClick={onItemClick}
          role={onItemClick ? "button" : undefined}
          tabIndex={onItemClick ? 0 : undefined}
          onKeyDown={onItemClick ? (e) => e.key === "Enter" && onItemClick() : undefined}
        >
          <span>
            {item.name === "fault_reported"
              ? "Fault reported"
              : item.name === "maintenance_required"
                ? "Maintenance required"
                : "Other issue"}
          </span>
          <b>{item.count}{item.highCritical ? ` (${item.highCritical} high/critical)` : ""}</b>
        </div>
      ))}
    </div>
  );
}

function AssetTableSection({
  rows,
  totalRows,
  page,
  totalPages,
  onPrev,
  onNext,
  search,
  onSearch,
  statusFilter,
  onStatusFilter,
  sortBy,
  onSort,
  siteFilter,
  onSiteFilter,
  siteOptions,
  clientFilter,
  onClientFilter,
  clientOptions,
  onResetFilters,
}) {
  return (
    <div className="asset-panel asset-table-panel">
      <h3>All assets</h3>
      <div className="asset-table-tools">
        <input value={search} onChange={(e) => onSearch(e.target.value)} placeholder="Search asset name, site, serial..." />
        <div style={{ minWidth: 180 }}>
          <SearchableSiteSelect
            value={siteFilter}
            onChange={(v) => onSiteFilter(v)}
            options={siteOptions.map((s) => ({ value: s, label: s }))}
            placeholder="Filter site"
          />
        </div>
        <select value={clientFilter} onChange={(e) => onClientFilter(e.target.value)}>
          <option value="">Filter client</option>
          {clientOptions.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <select value={sortBy} onChange={(e) => onSort(e.target.value)}>
          <option value="name">Sort by name</option>
          <option value="site">Sort by site</option>
          <option value="status">Sort by status</option>
          <option value="lastScan">Sort by last scan</option>
        </select>
        <button type="button" className="asset-reset-btn" onClick={onResetFilters}>Reset</button>
      </div>
      <div className="asset-filter-chips">
        {[
          { key: "all", label: "All" },
          { key: "active", label: "Active" },
          { key: "fault", label: "Fault reported" },
          { key: "maintenance", label: "Maintenance" },
          { key: "retired", label: "Retired" },
        ].map((x) => (
          <button key={x.key} type="button" className={statusFilter === x.key ? "active" : ""} onClick={() => onStatusFilter(x.key)}>
            {x.label}
          </button>
        ))}
      </div>
      <div className="asset-table-wrap">
        <table className="asset-table">
          <thead>
            <tr>
              <th>ASSET NUMBER</th>
              <th>ASSET NAME</th>
              <th>CLIENT</th>
              <th>DATE OF PURCHASE</th>
              <th>SERIAL NUMBER</th>
              <th>SITE</th>
              <th>CONDITION</th>
              <th>STATUS</th>
              <th>LAST SCAN</th>
            </tr>
          </thead>
          <tbody>
            {!rows.length ? (
              <tr><td colSpan={9} className="asset-empty">No assets found.</td></tr>
            ) : rows.map((r) => (
              <tr key={r._id || r.labelCode}>
                <td>{r.labelCode || "-"}</td>
                <td>{r.deviceName || "-"}</td>
                <td>{r.normalizedClient || "-"}</td>
                <td>{formatDate(r.dateOfPurchase)}</td>
                <td>{r.serialNumber || "-"}</td>
                <td>{r.normalizedSite || "-"}</td>
                <td>{r.deviceCondition || "-"}</td>
                <td><StatusPill status={r.normalizedStatus} /></td>
                <td>{formatDate(r.lastScanAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="asset-pagination">
        <span>{`Showing ${(page - 1) * 25 + 1}-${Math.min(page * 25, totalRows)} of ${totalRows}`}</span>
        <div>
          <button type="button" disabled={page <= 1} onClick={onPrev}>Prev</button>
          <button type="button" disabled={page >= totalPages} onClick={onNext}>Next</button>
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const cls = status === "active" ? "active" : status === "fault" ? "fault" : status === "maintenance" ? "maintenance" : "retired";
  return <span className={`asset-status-pill ${cls}`}>{toTitle(status)}</span>;
}

function toTitle(v) {
  return String(v || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function toPercent(a, b) {
  if (!b) return "0%";
  return `${Math.round((a / b) * 100)}%`;
}

function formatDate(v) {
  if (!v) return "-";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-GB");
}

function safeCsv(v) {
  const s = String(v ?? "");
  if (s.includes(",") || s.includes('"') || s.includes("\n")) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function buildSitePoints(sitesSummary, sitesMaster, normalizedAssets) {
  const coordsByName = new Map(
    (sitesMaster || []).map((s) => [
      String(s.siteName || "").trim().toLowerCase(),
      { lat: Number(s.latitude), lng: Number(s.longitude) },
    ])
  );
  const assetsBySite = new Map();
  (normalizedAssets || []).forEach((a) => {
    if (String(a.assetStatus || "").toLowerCase() === "retired") return;
    const key = String(a.normalizedSite || "").trim().toLowerCase();
    if (!key) return;
    if (!assetsBySite.has(key)) assetsBySite.set(key, []);
    assetsBySite.get(key).push(a);
  });

  return sitesSummary.map((s) => {
    const siteName = String(s.site || "");
    const assetsList = assetsBySite.get(siteName.trim().toLowerCase()) || [];
    const assets = assetsList.length;
    if (assets <= 0) return null;
    const faults = assetsList.filter((a) => {
      const condition = String(a.deviceCondition || "").toLowerCase();
      return condition === "fault" || condition === "maintenance required";
    }).length;
    const ratio = assets ? faults / assets : 0;
    const coords = coordsByName.get(siteName.trim().toLowerCase());
    const lat = Number(coords?.lat);
    const lng = Number(coords?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    if (lat < 49 || lat > 61 || lng < -10 || lng > 2) return null;

    const previewSize = 3;
    const assetsPreview = assetsList
      .slice(0, previewSize)
      .map((a) => ({
        labelCode: a.labelCode,
        deviceName: a.deviceName || "",
        siteName: a.normalizedSite || "",
      }));
    const assetsMoreCount = Math.max(0, assetsList.length - previewSize);

    return {
      site: siteName,
      assets,
      faults,
      maintained: assetsList.filter((a) => !Boolean(a.repairMaintenanceRequired)).length,
      lat,
      lng,
      high: ratio >= 0.08,
      r: Math.max(10, Math.min(22, 8 + assets / 15)),
      assetsPreview,
      assetsMoreCount: assetsMoreCount > 0 ? assetsMoreCount : 0,
    };
  }).filter(Boolean);
}

export default AssetManagementPage;
